import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  CommonMerchantGetMerchantData,
  PagedMerchantResponse,
  MerchantQueryParams,
} from '@/types/commonMerchantGetMerchantData'
import { commonMerchantApi } from '@/services/commonMerchantService'
import { AuthState } from '@/types/authState'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

const MERCHANT_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export const useMerchantStore = defineStore('merchant', () => {
  const _merchants = ref<CommonMerchantGetMerchantData[]>([])
  const _pagedResponse = ref<PagedMerchantResponse | null>(null)
  const _currentParams = ref<MerchantQueryParams>({})
  const _loading = ref(false)
  const _error = ref<ApiError | null>(null)
  const _lastFetchedAt = ref<number | null>(null)
  const includeDeleted = ref(
    localStorage.getItem('merchant_include_deleted') === 'true',
  )

  const getMerchants = () => _merchants.value
  const getPagedResponse = () => _pagedResponse.value
  const getCurrentParams = () => _currentParams.value

  const setMerchants = (newMerchants: CommonMerchantGetMerchantData[]) => {
    _merchants.value = newMerchants
  }

  function isCacheValid(params?: MerchantQueryParams): boolean {
    if (!_lastFetchedAt.value || _merchants.value.length === 0) return false
    const isStale = Date.now() - _lastFetchedAt.value > MERCHANT_CACHE_TTL_MS
    if (isStale) return false
    // Cache is only valid for the same query params
    const currentKey = JSON.stringify(_currentParams.value)
    const requestKey = JSON.stringify(params ?? {})
    return currentKey === requestKey
  }

  const fetchMerchants = async (
    authState: AuthState,
    params?: MerchantQueryParams,
  ) => {
    // Return cached data if still fresh and same params
    if (isCacheValid(params) && _pagedResponse.value) {
      return _pagedResponse.value
    }

    _currentParams.value = params ?? {}
    _loading.value = true
    _error.value = null
    try {
      const response = await commonMerchantApi.getMerchants(authState, params)
      _pagedResponse.value = response
      _merchants.value = response.Content ?? []
      _lastFetchedAt.value = Date.now()
      return response
    } catch (err) {
      _error.value = normalizeError(err)
      throw _error.value
    } finally {
      _loading.value = false
    }
  }

  function invalidateCache() {
    _lastFetchedAt.value = null
  }

  const setIncludeDeleted = (value: boolean) => {
    includeDeleted.value = value
    localStorage.setItem('merchant_include_deleted', String(value))
  }

  return {
    merchants: _merchants,
    pagedResponse: _pagedResponse,
    currentParams: _currentParams,
    loading: _loading,
    error: _error,
    includeDeleted,
    getMerchants,
    getPagedResponse,
    getCurrentParams,
    setMerchants,
    fetchMerchants,
    isCacheValid,
    invalidateCache,
    setIncludeDeleted,
  }
})
